import React from 'react';
import { Button, Divider, Grid, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import './login.form.scss';
import { AuthDto } from '../../../services/dtos/auth.dto';
import { object, string } from 'yup';
import { Link } from 'react-router-dom';
import ForgetPasswordDialog from '../forget-password/forget-password';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

//Props d'entrée de la fonction LoginForm
type Props = {
    handlerConnectionSubmit: (e: AuthDto) => Promise<void>;
    validate: boolean;
};

/**
 * Composant de type Form pour la connexion
 * @param handlerSubmit
 * @param validate
 * @constructor
 */
function LoginForm({ handlerConnectionSubmit, validate }: Props) {
    // Dialog forget password
    const [open, setOpen] = React.useState(false);

    // etat de la visibilité du mot de passe
    const [showPassword, setShowPassword] = React.useState(false);

    // mise à jour de la visibilité du mot de passe
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    //Handler qui gère l'ouverture de la popup "mdp perdu"
    const handleClickOpen = () => {
        setOpen(true);
    };

    //Handler qui gère la fermeture de la popup "mdp perdu"
    const handleClose = () => {
        setOpen(false);
    };

    // Définition des règles de validation
    const validationSchema = object({
        login: string().required("L'identifiant est obligatoire"),
        password: string().required('Le mot de passe est obligatoire'),
    });

    const login = localStorage.getItem('login');

    // Définition des valeurs et comportement du formulaire
    const formik = useFormik({
        initialValues: {
            login: login ?? '',
            password: '',
        },
        validationSchema: validationSchema,
        onSubmit: handlerConnectionSubmit,
    });

    // Création du formulaire
    return (
        // Encapsulation dans un Container
        <Paper className="container-login" sx={{ backgroundColor: '#3e5ca7' }}>
            <Typography variant="h5" sx={{ color: '#fff', textAlign: 'left' }}>
                Connectez-vous
            </Typography>
            {/*Formulaire*/}
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant={'body1'} sx={{ color: '#fff', textAlign: 'left' }}>
                            E-mail
                        </Typography>
                        {/*Champ texte Identifiant*/}
                        <TextField
                            id="login"
                            name="login"
                            placeholder="Saisir vore adresse mail"
                            inputProps={{ 'data-testid': 'input-login' }}
                            fullWidth
                            value={formik.values.login}
                            onChange={formik.handleChange}
                            error={formik.touched.login && Boolean(formik.errors.login)}
                            helperText={formik.touched.login && formik.errors.login}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant={'body1'} sx={{ color: '#fff', textAlign: 'left' }}>
                            Mot de passe
                        </Typography>
                        {/*Champ texte Mot de passe*/}
                        <TextField
                            id="password"
                            name="password"
                            placeholder="Saisir votre mot de passe"
                            fullWidth
                            inputProps={{ 'data-testid': 'input-password' }}
                            type={showPassword ? 'text' : 'password'}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            data-testid="eye-password"
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant={'button'}></Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant={'button'}>
                            <Button
                                variant={'text'}
                                component={Link}
                                style={{ width: '100%' }}
                                data-testid={'forgot-login'}
                                to="/identifiant-perdu"
                                sx={{ color: '#fff' }}
                            >
                                Identifiant oublié&nbsp;?
                            </Button>
                            <Button
                                onClick={handleClickOpen}
                                style={{ width: '100%' }}
                                data-testid={'forgot-password'}
                                sx={{ color: '#fff' }}
                            >
                                Mot de passe oublié&nbsp;?
                            </Button>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Button
                            type="submit"
                            variant="outlined"
                            data-testid={'submit'}
                            disabled={validate}
                            sx={{ padding: '12px', minWidth: '150px' }}
                        >
                            Connexion
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider variant="middle" sx={{ borderColor: 'white', marginBottom: '10px' }} />
                        <Typography variant="body1" sx={{ color: '#fff', textAlign: 'left' }}>
                            Vous n'êtes pas encore inscrit ?
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#fff', textAlign: 'left' }}>
                            Créez votre espace et accédez à vos services en ligne !
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}></Grid>
                    <Grid item xs={12} md={4}>
                        <Button component={Link} variant="outlined" to="/creer-espace-usage" sx={{ padding: '12px' }}>
                            Créer mon compte
                        </Button>
                    </Grid>
                    <ForgetPasswordDialog open={open} handleClose={handleClose} />
                </Grid>
            </form>
        </Paper>
    );
}

export default LoginForm;
